// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for CollaborationActivity entity
 */
type CollaborationActivityConfig = BaseMetricConfig;

/**
 * CollaborationActivity Entity - Encapsulates all collaboration activity-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the collaboration activity ecosystem metric.
 */
export class CollaborationActivity extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'collaboration_activity' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: CollaborationActivityConfig = {
    metricType: 'collaboration_activity',
    display: {
      displayName: 'Collaboration Activity',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of documentation collaboration activities for this project are from our company?',
        scoringRules: [
          'Each 0.1% of collaboration activities to the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 collaboration activities = 0 points',
          '0.000001 - 0.1% of collaboration activities = 1.1 point',
          '0.2% of collaboration activities = 1.2 points',
          '2.5% of collaboration activities = 3.5 points',
          '20.0% of collaboration activities = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no collaboration activity for this project.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company contributed {percentage}% of all collaboration activities for this project.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      },
      lowValue: {
        description: `Our company has not enough collaboration activity for this project.`,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for collaboration activity
   *
   * Note: Unlike other ecosystem metrics, Collaboration Activity does NOT check isLfProject
   * because this data comes from GitHub/GitLab repositories and exists for ANY project,
   * regardless of LF membership status.
   *
   * @param percentage - The percentage of collaboration activities from our company
   * @param allTimeTotal - Total historical collaboration activities for the project
   * @param _isLfProject - Whether the project is an LF project (unused for this metric)
   * @param level - Pre-calculated score level from backend
   * @param _projectType - Project type (unused for this metric)
   * @param points - Optional collaboration activity points from backend
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, _isLfProject: boolean, level: ScoreLevel, _projectType?: string | null, points?: number): MetricBottomSummary {
    // Note: We intentionally do NOT check isLfProject here because Collaboration Activity
    // data is derived from code/documentation contributions on project repositories,
    // which exist for any project (LF or non-LF).

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    // Use pre-calculated level from backend
    const styles = this.getStylesByLevel(level);

    if (numericValue === 0) {
      // Check if company has minimal activity (points > 0 but percentage rounds to 0)
      if (points !== undefined && points > 0) {
        return {
          title: this.CONFIG.display.displayName,
          description: this.CONFIG.bottomSummary.lowValue!.description,
          icon: styles.icon,
          iconColor: styles.iconColor,
          borderColor: styles.borderColor
        };
      }

      return {
        title: this.CONFIG.display.displayName,
        description: this.CONFIG.bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentageValue
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
